import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { CRMUser } from '../../types';
import { mentionAffiliationLabel } from '../../utils/mentionAffiliation';
import { placeAnnotationMentionMenu } from '../../utils/annotationMentionMenu';

interface AnnotationMentionDropdownProps {
  anchor: HTMLElement | null;
  users: Array<Pick<CRMUser, 'uid' | 'displayName' | 'email' | 'role' | 'company' | 'supplierOrgId'>>;
  selectedIndex: number;
  emptyLabel: string;
  onSelect: (user: AnnotationMentionDropdownProps['users'][number]) => void;
}

function placeFromAnchor(anchor: HTMLElement, menu?: HTMLElement | null) {
  return placeAnnotationMentionMenu(
    anchor.getBoundingClientRect(),
    { width: window.innerWidth, height: window.innerHeight },
    menu?.offsetHeight,
  );
}

export function AnnotationMentionDropdown({
  anchor,
  users,
  selectedIndex,
  emptyLabel,
  onSelect,
}: AnnotationMentionDropdownProps) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<ReturnType<typeof placeAnnotationMentionMenu> | null>(null);

  useLayoutEffect(() => {
    if (!anchor) return undefined;
    const sync = () => setPos(placeFromAnchor(anchor, menuRef.current));
    sync();
    window.addEventListener('resize', sync);
    window.addEventListener('scroll', sync, true);
    return () => {
      window.removeEventListener('resize', sync);
      window.removeEventListener('scroll', sync, true);
    };
  }, [anchor, users.length, selectedIndex]);

  useLayoutEffect(() => {
    const selected = menuRef.current?.querySelector<HTMLElement>('[data-selected="true"]');
    selected?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  if (!anchor) return null;

  return createPortal(
    <div
      ref={menuRef}
      className="annotation-mention-dropdown"
      data-annotation-mention-menu="true"
      role="listbox"
      style={pos ? {
        top: pos.top,
        left: pos.left,
        width: pos.width,
        maxHeight: pos.maxHeight,
      } : undefined}
    >
      {users.length > 0 ? (
        users.map((user, i) => {
          const selected = i === selectedIndex;
          const affiliation = mentionAffiliationLabel(user);
          return (
            <button
              key={user.uid}
              type="button"
              role="option"
              aria-selected={selected}
              data-selected={selected ? 'true' : undefined}
              className={`annotation-mention-option${selected ? ' selected' : ''}`}
              onMouseDown={event => event.preventDefault()}
              onClick={() => onSelect(user)}
            >
              <span className="annotation-mention-option-name">
                {user.displayName || user.email || user.uid}
              </span>
              {affiliation ? (
                <span className="annotation-mention-option-affil">{affiliation}</span>
              ) : null}
            </button>
          );
        })
      ) : (
        <div className="annotation-mention-empty">{emptyLabel}</div>
      )}
    </div>,
    document.body,
  );
}
